import { useCallback, useEffect, useRef, useState } from 'react';

export type SaveState = 'idle' | 'pending' | 'saving' | 'saved' | 'error';

export const saveStateLabel: Record<SaveState, string> = {
  idle: '',
  pending: 'Unsaved changes',
  saving: 'Saving…',
  saved: 'Saved',
  error: 'Could not save',
};

export const AUTOSAVE_DELAY_MS = 1000;

/**
 * Slower cadence used to retry after a failed save, so a backend restart or a
 * dropped connection costs the writer nothing but a few seconds.
 */
export const AUTOSAVE_RETRY_DELAY_MS = 5000;

interface Options<T> {
  /** Persists the current values. Reject to surface the error state. */
  save: (values: T) => Promise<unknown>;
  /** Autosave is skipped entirely while this is false (locked or read-only forms). */
  enabled?: boolean;
  delayMs?: number;
}

/**
 * Debounced autosave with an explicit save state, shared by the assessor and
 * report-writer forms. The caller owns the field values; this hook only decides
 * when to flush them and reports how that went.
 *
 * Values are read through a ref at flush time so a save always sends the latest
 * keystroke rather than whatever was current when the timer was set.
 */
export function useDebouncedAutosave<T>({ save, enabled = true, delayMs = AUTOSAVE_DELAY_MS }: Options<T>) {
  const [saveState, setSaveState] = useState<SaveState>('idle');
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingRef = useRef<T | null>(null);
  const saveRef = useRef(save);
  const enabledRef = useRef(enabled);
  const flushRef = useRef<() => void>(() => {});

  saveRef.current = save;
  enabledRef.current = enabled;

  const flush = useCallback(async () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    const values = pendingRef.current;
    if (values === null || !enabledRef.current) return;
    pendingRef.current = null;

    setSaveState('saving');
    try {
      await saveRef.current(values);
      // A keystroke during the request leaves new pending values behind.
      setSaveState(pendingRef.current === null ? 'saved' : 'pending');
    } catch {
      // Put the values back rather than dropping them, so the edit survives a
      // failed request, then retry on the slower timer. Keystrokes that landed
      // during the request are newer and win.
      if (pendingRef.current === null) pendingRef.current = values;
      setSaveState('error');
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => { flushRef.current(); }, AUTOSAVE_RETRY_DELAY_MS);
    }
  }, []);

  flushRef.current = () => { void flush(); };

  const schedule = useCallback((values: T) => {
    if (!enabledRef.current) return;
    pendingRef.current = values;
    setSaveState('pending');
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => { void flush(); }, delayMs);
  }, [flush, delayMs]);

  const cancel = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = null;
    pendingRef.current = null;
  }, []);

  // Save anything still pending when the user navigates away mid-sentence.
  useEffect(() => () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (pendingRef.current !== null && enabledRef.current) {
      void saveRef.current(pendingRef.current).catch(() => {});
    }
  }, []);

  return { saveState, setSaveState, schedule, flush, cancel, hasPending: () => pendingRef.current !== null };
}
